import { cn } from "@/lib/utils";
import { SERIES_BG } from "@/components/dashboard/widgets/opsColors";

/**
 * Daily scan counts as bars. One series in the validated navy, so no legend:
 * the caller's heading names it and prints the total in text, because bar
 * height alone is relative to the busiest day.
 *
 * Each day is a full-height column carrying the tooltip, so the hover target
 * is the whole slot rather than a 3px sliver. Zero days are a hairline so the
 * run of days stays readable; gaps keep neighbouring bars distinct where
 * there is room for them (see dayBarSpacing).
 */
/**
 * Gap between bars and the narrowest a bar may get, in px, for `count` bars.
 * A flex item never shrinks below its min-width, so fixed spacing made the
 * 1y view (365 bars) about 1458px wide and pushed the newest days off the
 * sheet. Spacing tightens as the count grows so every period fits a phone.
 */
export function dayBarSpacing(count: number): { gapPx: number; minBarPx: number } {
  if (count <= 40) return { gapPx: 2, minBarPx: 2 };
  if (count <= 120) return { gapPx: 1, minBarPx: 1 };
  return { gapPx: 0, minBarPx: 0 };
}

export function DayBars({
  values,
  height = 24,
  labels,
  className,
}: {
  values: number[];
  height?: number;
  /** Optional per-day tooltip, e.g. "Oct 12: 3 scans". */
  labels?: string[];
  className?: string;
}) {
  const max = Math.max(1, ...values);
  const { gapPx, minBarPx } = dayBarSpacing(values.length);
  return (
    <div className={cn("flex items-stretch", className)} style={{ height, gap: gapPx }} aria-hidden="true">
      {values.map((v, i) => (
        <div key={i} title={labels?.[i]} className="flex flex-1 items-end" style={{ minWidth: minBarPx }}>
          <div
            className={cn("w-full rounded-t-[2px]", v ? SERIES_BG : "bg-border")}
            style={{ height: v ? `${Math.max(12, (v / max) * 100)}%` : 1 }}
          />
        </div>
      ))}
    </div>
  );
}
